import { useState, useEffect } from "react";
import Dropdown from "./Dropdown";
import InputData from "./InputData";
import InputDataControl from "../support-extra/components/InputDataControl";
import InputTextArea from "./InputTextArea";
import ButtonIconV from "./ButtonIconV";
import {
  refractiveStructure,
  userData,
  selectedOptic,
  inputOnEnter,
  areObjectsEqual,
  focusElement,
  validateRefractiveData,
  newRefractiveRXExists,
} from "../js/utils";
import { RETINOSCOPYLIST } from "../js/utils";
import { post } from "../js/post";
import moment from "moment";
import "../css/BasicOptometry.css";
import DropdownFilter from "./DropdownFilter";
import LoadingScreen from "./LoadingScreen";
import TooltipBox from "./Tooltip";
import { useOptometrists } from "../hooks/useOptometrists";
import { AdvancedOptometryReportPopup } from "./AdvancedOptometryReportPopup";

export default function AdvancedOptometryRefractive(props) {
  const [currentRefractive, setCurrentRefractive] =
    useState(refractiveStructure);
  const [currentRefractiveEdit, setCurrentRefractiveEdit] =
    useState(refractiveStructure);
  const [dateHistoryList, setDateHistoryList] = useState([]);
  const optometristList = [{ id: "", text: "" }, ...useOptometrists()];
  const [selectedDate, setSelectedDate] = useState({ id: "", text: "" });
  const [refractiveId, setRefractiveId] = useState(props.refractiveId);
  const [loadingData, setLoadingData] = useState(true);
  const [reportPopup, setReportPopup] = useState(false);

  const getRefractiveHistory = async () => {
    const response = await post("ListarFechasHistoricoRefractivo.php", {
      token: userData.token,
      idPaciente: props.patientId,
      optica: selectedOptic.id,
    });
    if (response.length > 0) {
      const found = response.find((e) => {
        return e.idRefractivo === refractiveId;
      });
      if (found) {
        setSelectedDate({
          id: found.idRefractivo,
          text: found.fecha,
        });
        setRefractiveId(found.idRefractivo);
      }
    }
    setDateHistoryList(response);
  };

  const getRefractive = async (refractiveId) => {
    const response = await post("ObtenerRefractivo.php", {
      token: userData.token,
      optica: selectedOptic.id,
      idRefractivo: refractiveId,
    });

    setCurrentRefractiveEdit({
      ...response,
      actualizarOptometriaBasica: false,
      idPaciente: props.patientId,
      idRefractivo: refractiveId,
    });
    setCurrentRefractive({
      ...response,
      actualizarOptometriaBasica: false,
      idPaciente: props.patientId,
      idRefractivo: refractiveId,
    });
  };

  const checkNewRX = async () => {
    if (newRefractiveRXExists(currentRefractiveEdit)) {
      return new Promise((resolve) => {
        props.createPopupQuestion(
          `¿Desea actualizar la ficha de optometría asociada al estudio refractivo?`,
          () => {
            props.removePopupQuestion();
            resolve(true);
          },
          () => {
            props.removePopupQuestion();
            resolve(false);
          },
          "Sí",
          "No",
        );
      });
    }
    return false;
  };

  const saveRefractive = async () => {
    const userChoice = await checkNewRX();
    const response = await post("GuardarRefractivo.php", {
      token: userData.token,
      optica: selectedOptic.id,
      idRefractivo: refractiveId,
      idPaciente: props.patientId,
      ...currentRefractiveEdit,
      actualizarOptometriaBasica: userChoice,
    });
    setSelectedDate({
      id: response.idRefractivo,
      text: currentRefractiveEdit.fecha,
    });
    setRefractiveId(response.idRefractivo);
    setCurrentRefractive({
      ...currentRefractiveEdit,
      actualizarOptometriaBasica: userChoice,
    });
    setCurrentRefractiveEdit((prev) => ({
      ...prev,
      actualizarOptometriaBasica: userChoice,
    }));
    props.saveLastRefractiveIdVisited(response.idRefractivo);
    props.createPopupQuestion(
      `Los cambios se han guardado con éxito.`,
      props.removePopupQuestion,
      null,
      "Entendido",
    );
  };

  const deleteRefractive = async () => {
    await post("EliminarRefractivo.php", {
      token: userData.token,
      optica: selectedOptic.id,
      idRefractivo: refractiveId,
      idUsuario: userData.id,
    });
  };

  const selectRefractiveAfterDeleting = async () => {
    const response = await post("ListarFechasHistoricoRefractivo.php", {
      token: userData.token,
      idPaciente: props.patientId,
      optica: selectedOptic.id,
    });
    if (response.length > 0) {
      setSelectedDate({
        id: response[0].idRefractivo,
        text: response[0].fecha,
      });
      setRefractiveId(response[0].idRefractivo);
      setDateHistoryList(response);
      await getRefractive(response[0].idRefractivo);
      props.saveLastRefractiveIdVisited(response[0].idRefractivo);
    } else {
      setSelectedDate({
        id: -1,
        text: moment().format("YYYY-MM-DD HH:mm:ss"),
      });
      setRefractiveId(-1);
      props.saveLastRefractiveIdVisited(-1);
      setCurrentRefractive(refractiveStructure);
      setCurrentRefractiveEdit(refractiveStructure);
    }
  };

  const cancelChanges = async () => {
    if (dateHistoryList.length > 0) {
      // 1. Si estamos creando una ficha, cargamos la última.
      if (refractiveId === -1) {
        setSelectedDate({
          id: dateHistoryList[0].idRefractivo,
          text: dateHistoryList[0].fecha,
        });
        setRefractiveId(dateHistoryList[0].idRefractivo);
        props.saveLastRefractiveIdVisited(dateHistoryList[0].idRefractivo);
        await getRefractive(dateHistoryList[0].idRefractivo);
      } else {
        // 2. Si estamos viendo una ficha, cancelamos los cambios.
        setCurrentRefractiveEdit(currentRefractive);
      }
    } else {
      setCurrentRefractiveEdit(currentRefractive);
    }
  };

  const resetRefractiveState = () => {
    setCurrentRefractiveEdit({
      ...refractiveStructure,
      fecha: moment().format("YYYY-MM-DD HH:mm:ss"),
    });
    setCurrentRefractive({
      ...refractiveStructure,
      fecha: moment().format("YYYY-MM-DD HH:mm:ss"),
    });
    setRefractiveId(-1);
    props.saveLastRefractiveIdVisited(-1);
    setSelectedDate({
      id: -1,
      text: moment().format("YYYY-MM-DD HH:mm:ss"),
    });
  };

  function focusRequiredField() {
    const fields = [
      { value: currentRefractiveEdit.optometrista, elementId: 53 },
    ];

    const emptyField = fields.find((field) => field.value === "");
    if (emptyField) {
      focusElement(emptyField.elementId);
    }
  }

  const printFile = async (data) => {
    if (refractiveId === -1) return;
    const response = await post(
      "/support-extra/gestionDocumental/GenerarFichaOptometriaAvanzada.php",
      {
        token: userData.token,
        idOptica: selectedOptic.id,
        idPaciente: props.patientId,
        anamnesis: data.anamnesis,
        anamnesisNotas: data.anamnesisNotes,
        refractivo: data.refractive,
        refractivoNotas: data.refractiveNotes,
        oculomotor: data.oculomotor,
        oculomotorNotas: data.oculomotorNotes,
        binocular: data.binocular,
        binocularNotas: data.binocularNotes,
        acomodativo: data.accommodative,
        acomodativoNotas: data.accommodativeNotes,
        sensorial: data.sensory,
        sensorialNotas: data.sensoryNotes,
        saludOcular: data.eyeHealth,
        saludOcularNotas: data.eyeHealthNotes,
        email: data.email,
      },
    );
    // console.log(
      // "GENERANDO: ",
      // {
        // token: userData.token,
        // idOptica: selectedOptic.id,
        // idPaciente: props.patientId,
        // anamnesis: data.anamnesis,
        // anamnesisNotas: data.anamnesisNotes,
        // refractivo: data.refractive,
        // refractivoNotas: data.refractiveNotes,
        // oculomotor: data.oculomotor,
        // oculomotorNotas: data.oculomotorNotes,
        // binocular: data.binocular,
        // binocularNotas: data.binocularNotes,
        // acomodativo: data.accommodative,
        // acomodativoNotas: data.accommodativeNotes,
        // sensorial: data.sensory,
        // sensorialNotas: data.sensoryNotes,
        // saludOcular: data.eyeHealth,
        // saludOcularNotas: data.eyeHealthNotes,
        // email: data.email,
      // },
      // response,
    // );
    if (response.error !== "") {
      props.createPopupQuestion(
        response.error,
        () => {
          props.removePopupQuestion();
        },
        null,
        "Entendido",
      );
    } else {
      const a = document.createElement("a");
      a.href = response.fichero;
      a.download = `ficha_optometria_avanzada_refractivo.pdf`;
      a.click();
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await getRefractiveHistory();
      if (refractiveId !== -1) {
        await getRefractive(refractiveId);
      } else {
        setSelectedDate({
          id: -1,
          text: moment().format("YYYY-MM-DD HH:mm:ss"),
        });
        setCurrentRefractiveEdit({
          ...currentRefractiveEdit,
          fecha: moment().format("YYYY-MM-DD HH:mm:ss"),
        });
        setCurrentRefractive({
          ...currentRefractive,
          fecha: moment().format("YYYY-MM-DD HH:mm:ss"),
        });
      }
      focusElement(54);
      setLoadingData(false);
    };
    loadData();
  }, [refractiveId]);

  useEffect(() => {
    props.pendingChanges({
      changes: !areObjectsEqual(currentRefractive, currentRefractiveEdit),
      dataToSave: currentRefractiveEdit,
    });
  }, [currentRefractiveEdit, props.pendingChanges]);

  useEffect(() => {
    if (props.diagnosisValue !== currentRefractiveEdit.diagnostico) {
      setCurrentRefractiveEdit({
        ...currentRefractiveEdit,
        diagnostico: props.diagnosisValue,
      });
    }
  }, [props.diagnosisValue]);

  return (
    <>
      {loadingData && (
        <LoadingScreen message="Cargando ficha de refractivo..."></LoadingScreen>
      )}
      {!loadingData && (
        <div className="tab-content-screen bg-transparent">
          <div className="content-grid">
            {reportPopup && (
              <AdvancedOptometryReportPopup
                type="refractive"
                createPopupQuestion={props.createPopupQuestion}
                removePopupQuestion={props.removePopupQuestion}
                onExport={async (data) => {
                  await printFile(data);
                }}
                onClose={() => {
                  setReportPopup(false);
                }}
              />
            )}
            <div
              className="popup-screen-box external-container"
              style={{ gridColumn: "1/span 3", gridRow: "1/span 1" }}
            >
              <div className="popup-screen-box-info">
                <div className="cell-extra">
                  <span className="label">Creación</span>
                </div>
                <div className="cell-extra w-220">
                  <div className="input-data input-full">
                    <input
                      type="datetime-local"
                      max="9999-12-31T23:59"
                      step="1"
                      tabIndex={51}
                      className="input-field"
                      value={currentRefractiveEdit.fecha}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          fecha: e.target.value,
                        });
                      }}
                      onKeyUp={(e) => {
                        inputOnEnter(e);
                      }}
                    ></input>
                  </div>
                </div>
                <div className="cell-extra">
                  <span className="label">
                    Histórico ({dateHistoryList.length})
                  </span>
                </div>
                <div className="cell-extra">
                  <DropdownFilter
                    tabIndex={52}
                    selectedOptions={[]}
                    optionList={dateHistoryList.map((item) => ({
                      id: item.idRefractivo,
                      text: moment(item.fecha, "YYYY-MM-DD HH:mm:ss").format(
                        "DD/MM/YYYY HH:mm:ss",
                      ),
                      active: selectedDate.id === item.idRefractivo,
                    }))}
                    size={2}
                    op={moment(selectedDate.text, "YYYY-MM-DD HH:mm:ss").format(
                      "DD/MM/YYYY HH:mm:ss",
                    )}
                    disabled={refractiveId === -1}
                    onChange={async (e) => {
                      setLoadingData(true);
                      setCurrentRefractiveEdit(refractiveStructure);
                      setSelectedDate({
                        id: e.id,
                        text: moment(e.text, "DD/MM/YYYY HH:mm:ss").format(
                          "YYYY-MM-DD HH:mm:ss",
                        ),
                      });
                      setRefractiveId(e.id);
                      props.saveLastRefractiveIdVisited(e.id);
                    }}
                  ></DropdownFilter>
                </div>
                <div className="cell-extra cell-extra-optometrista">
                  <span className="label">Optometrista (*)</span>
                </div>
                <div className="cell-extra combined-cells-3">
                  <Dropdown
                    tabIndex={53}
                    optionList={optometristList}
                    size={0}
                    op={currentRefractiveEdit.optometrista}
                    onChange={(e) => {
                      setCurrentRefractiveEdit({
                        ...currentRefractiveEdit,
                        optometrista: e.text,
                      });
                    }}
                  ></Dropdown>
                </div>
              </div>
            </div>
            <div
              className="popup-screen-box external-container"
              style={{ gridColumn: "4/span 3", gridRow: "1/span 1" }}
            >
              <ButtonIconV
                text="Nuevo"
                icon={
                  refractiveId !== -1
                    ? "/images/create-blue.svg"
                    : "/images/create-gray.svg"
                }
                onClick={() => {
                  if (refractiveId !== -1) {
                    if (
                      !areObjectsEqual(currentRefractive, currentRefractiveEdit)
                    ) {
                      props.createPopupQuestion(
                        `Existen cambios sin guardar en la ficha actual. ¿Desea guardar los cambios antes de crear una nueva ficha?`,
                        async () => {
                          await saveRefractive();
                          props.removePopupQuestion();
                          resetRefractiveState();
                        },
                        async () => {
                          props.removePopupQuestion();
                          resetRefractiveState();
                        },
                        "Sí",
                        "No",
                      );
                    } else {
                      resetRefractiveState();
                    }
                  }
                }}
              ></ButtonIconV>
              <ButtonIconV
                text="Guardar"
                icon="/images/save-blue.svg"
                onClick={async () => {
                  if (
                    currentRefractiveEdit.optometrista !== "" &&
                    validateRefractiveData(currentRefractiveEdit)
                  ) {
                    await saveRefractive();
                    await getRefractiveHistory();
                    props.updateTab();
                  } else {
                    props.createPopupQuestion(
                      `Debe seleccionar un optometrista y rellenar al menos un campo de la ficha.`,
                      () => {
                        focusRequiredField();
                        props.removePopupQuestion();
                      },
                      null,
                      "Entendido",
                    );
                  }
                }}
              ></ButtonIconV>
              <ButtonIconV
                text="Cancelar"
                icon="/images/cancel-blue.svg"
                onClick={() => {
                  if (
                    dateHistoryList.length === 0 &&
                    areObjectsEqual(currentRefractive, currentRefractiveEdit)
                  ) {
                    props.removePopupQuestion();
                  } else if (
                    !areObjectsEqual(
                      currentRefractive,
                      currentRefractiveEdit,
                    ) ||
                    refractiveId === -1
                  ) {
                    props.createPopupQuestion(
                      `¿Desea cancelar los cambios realizados?`,
                      async () => {
                        await cancelChanges();
                        props.removePopupQuestion();
                      },
                      props.removePopupQuestion,
                      "Sí",
                      "No",
                    );
                  }
                }}
              ></ButtonIconV>
              <ButtonIconV
                text="Recargar"
                icon={
                  refractiveId !== -1
                    ? "/images/update-blue.svg"
                    : "/images/update-gray.svg"
                }
                onClick={() => {
                  if (refractiveId !== -1) {
                    props.createPopupQuestion(
                      `¿Desea recargar esta ficha de refractivo?`,
                      async () => {
                        await getRefractive(refractiveId);
                        props.removePopupQuestion();
                      },
                      props.removePopupQuestion,
                      "Sí",
                      "No",
                    );
                  }
                }}
              ></ButtonIconV>
              <ButtonIconV
                text="Eliminar"
                icon={
                  refractiveId !== -1
                    ? "/images/remove-blue.svg"
                    : "/images/remove-gray.svg"
                }
                onClick={() => {
                  if (refractiveId !== -1) {
                    props.createPopupQuestion(
                      `¿Desea eliminar esta ficha de refractivo?`,
                      async () => {
                        await deleteRefractive();
                        await getRefractiveHistory();
                        await selectRefractiveAfterDeleting();
                        props.updateTab();
                        props.removePopupQuestion();
                      },
                      props.removePopupQuestion,
                      "Sí",
                      "No",
                    );
                  }
                }}
              ></ButtonIconV>
              <ButtonIconV
                text="Notas"
                icon={
                  refractiveId !== -1
                    ? "/images/write-blue.svg"
                    : "/images/write-gray.svg"
                }
                onClick={() => {
                  if (refractiveId !== -1) {
                    props.openDiagnosisPopup(currentRefractiveEdit.diagnostico);
                  }
                }}
              ></ButtonIconV>
              <ButtonIconV
                text="Imprimir"
                icon={
                  refractiveId !== -1
                    ? "/images/print-blue.svg"
                    : "/images/print-gray.svg"
                }
                onClick={() => {
                  if (refractiveId !== -1) {
                    setReportPopup(true);
                  }
                }}
              ></ButtonIconV>
            </div>
            <div
              className="popup-screen-box external-container"
              style={{ gridColumn: "1/span 3", gridRow: "2/span 1" }}
            >
              <div className="opt-table-container">
                <div className="opt-table-title">Refracción objetiva</div>
                <div className="opt-table-content grid-9" id="ref-grid">
                  <div className="cell"></div>
                  <div className="cell opt-table-subtitle combined-cells-3">
                    Retinoscopía
                  </div>
                  <div className="cell"></div>
                  <div className="cell"></div>
                  <div className="cell opt-table-subtitle combined-cells-3">
                    Autorrefractometría
                  </div>
                  <div className="cell"></div>
                  <div className="cell cell-label">Esf</div>
                  <div className="cell cell-label">Cil</div>
                  <div className="cell cell-label">Eje</div>
                  <div className="cell"></div>
                  <div className="cell"></div>
                  <div className="cell cell-label">Esf</div>
                  <div className="cell cell-label">Cil</div>
                  <div className="cell cell-label">Eje</div>
                  <div className="cell cell-label cell-start-content">
                    Ojo D
                  </div>
                  <div className="cell">
                    <InputDataControl
                      tabIndex={54}
                      id="cell"
                      type="type-optofloat"
                      size={0}
                      input={currentRefractiveEdit.retinoscopia_esfera_od}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={props.createPopupQuestion}
                      removePopupQuestion={props.removePopupQuestion}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          retinoscopia_esfera_od: e,
                        });
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell">
                    <InputDataControl
                      tabIndex={55}
                      id="cell"
                      type="type-optofloat"
                      size={0}
                      input={currentRefractiveEdit.retinoscopia_cilindro_od}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={props.createPopupQuestion}
                      removePopupQuestion={props.removePopupQuestion}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          retinoscopia_cilindro_od: e,
                        });
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell">
                    <InputData
                      tabIndex={56}
                      id="cell"
                      type="type-only-positive-numbers"
                      maxLength={3}
                      size={0}
                      input={currentRefractiveEdit.retinoscopia_eje_od}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          retinoscopia_eje_od: e,
                        });
                      }}
                    ></InputData>
                  </div>
                  <div className="cell"></div>
                  <div className="cell cell-label cell-start-content">
                    Ojo D
                  </div>
                  <div className="cell">
                    <InputDataControl
                      tabIndex={61}
                      id="cell"
                      type="type-optofloat"
                      size={0}
                      input={
                        currentRefractiveEdit.autorrefractometria_esfera_od
                      }
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={props.createPopupQuestion}
                      removePopupQuestion={props.removePopupQuestion}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          autorrefractometria_esfera_od: e,
                        });
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell">
                    <InputDataControl
                      tabIndex={62}
                      id="cell"
                      type="type-optofloat"
                      size={0}
                      input={
                        currentRefractiveEdit.autorrefractometria_cilindro_od
                      }
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={props.createPopupQuestion}
                      removePopupQuestion={props.removePopupQuestion}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          autorrefractometria_cilindro_od: e,
                        });
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell">
                    <InputData
                      tabIndex={63}
                      id="cell"
                      type="type-only-positive-numbers"
                      maxLength={3}
                      size={0}
                      input={currentRefractiveEdit.autorrefractometria_eje_od}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          autorrefractometria_eje_od: e,
                        });
                      }}
                    ></InputData>
                  </div>
                  <div className="cell cell-label cell-start-content">
                    Ojo I
                  </div>
                  <div className="cell">
                    <InputDataControl
                      tabIndex={57}
                      id="cell"
                      type="type-optofloat"
                      size={0}
                      input={currentRefractiveEdit.retinoscopia_esfera_oi}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={props.createPopupQuestion}
                      removePopupQuestion={props.removePopupQuestion}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          retinoscopia_esfera_oi: e,
                        });
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell">
                    <InputDataControl
                      tabIndex={58}
                      id="cell"
                      type="type-optofloat"
                      size={0}
                      input={currentRefractiveEdit.retinoscopia_cilindro_oi}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={props.createPopupQuestion}
                      removePopupQuestion={props.removePopupQuestion}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          retinoscopia_cilindro_oi: e,
                        });
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell">
                    <InputData
                      tabIndex={59}
                      id="cell"
                      type="type-only-positive-numbers"
                      maxLength={3}
                      size={0}
                      input={currentRefractiveEdit.retinoscopia_eje_oi}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          retinoscopia_eje_oi: e,
                        });
                      }}
                    ></InputData>
                  </div>
                  <div className="cell"></div>
                  <div className="cell cell-label cell-start-content">
                    Ojo I
                  </div>
                  <div className="cell">
                    <InputDataControl
                      tabIndex={64}
                      id="cell"
                      type="type-optofloat"
                      size={0}
                      input={
                        currentRefractiveEdit.autorrefractometria_esfera_oi
                      }
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={props.createPopupQuestion}
                      removePopupQuestion={props.removePopupQuestion}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          autorrefractometria_esfera_oi: e,
                        });
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell">
                    <InputDataControl
                      tabIndex={65}
                      id="cell"
                      type="type-optofloat"
                      size={0}
                      input={
                        currentRefractiveEdit.autorrefractometria_cilindro_oi
                      }
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={props.createPopupQuestion}
                      removePopupQuestion={props.removePopupQuestion}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          autorrefractometria_cilindro_oi: e,
                        });
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell">
                    <InputData
                      tabIndex={66}
                      id="cell"
                      type="type-only-positive-numbers"
                      maxLength={3}
                      size={0}
                      input={currentRefractiveEdit.autorrefractometria_eje_oi}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          autorrefractometria_eje_oi: e,
                        });
                      }}
                    ></InputData>
                  </div>
                  <div className="cell"></div>
                  <div className="cell combined-cells-3">
                    <Dropdown
                      id="retinoscopia"
                      tabIndex={60}
                      optionList={RETINOSCOPYLIST}
                      size={0}
                      op={currentRefractiveEdit.tipo_retinoscopia}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          tipo_retinoscopia: e.text,
                        });
                      }}
                    ></Dropdown>
                  </div>
                  <div className="cell combined-cells-4"></div>
                  <div className="cell opt-table-subtitle combined-cells-9 cell-start-content">
                    Observaciones
                  </div>
                  <div className="cell combined-cells-9">
                    <InputTextArea
                      tabIndex={67}
                      maxLength={400}
                      id="refraccion-objetiva-observaciones"
                      type="type-full"
                      size={0}
                      input={
                        currentRefractiveEdit.observaciones_refraccion_objetiva
                      }
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          observaciones_refraccion_objetiva: e,
                        });
                      }}
                    ></InputTextArea>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="popup-screen-box external-container"
              style={{ gridColumn: "1/span 6", gridRow: "3/span 1" }}
            >
              <div style={{ display: "flex", gap: 20 }}>
                <div className="opt-table-container w-50">
                  <div className="opt-table-title">
                    Refracción subjetiva binocular
                  </div>
                  <div className="opt-table-content grid-9" id="ref-grid">
                    <div className="cell"></div>
                    <div className="cell opt-table-subtitle combined-cells-3">
                      Filtros polarizados
                    </div>
                    <div className="cell"></div>
                    <div className="cell"></div>
                    <div className="cell opt-table-subtitle combined-cells-3">
                      Retrasado
                    </div>
                    <div className="cell"></div>
                    <div className="cell cell-label">Esf</div>
                    <div className="cell cell-label">Cil</div>
                    <div className="cell cell-label">Eje</div>
                    <div className="cell"></div>
                    <div className="cell"></div>
                    <div className="cell cell-label">Esf</div>
                    <div className="cell cell-label">Cil</div>
                    <div className="cell cell-label">Eje</div>
                    <div className="cell cell-label cell-start-content">
                      Ojo D
                    </div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={68}
                        id="cell"
                        type="type-optofloat"
                        size={0}
                        input={
                          currentRefractiveEdit.filtros_polarizados_esfera_od
                        }
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={props.createPopupQuestion}
                        removePopupQuestion={props.removePopupQuestion}
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            filtros_polarizados_esfera_od: e,
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={69}
                        id="cell"
                        type="type-optofloat"
                        size={0}
                        input={
                          currentRefractiveEdit.filtros_polarizados_cilindro_od
                        }
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={props.createPopupQuestion}
                        removePopupQuestion={props.removePopupQuestion}
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            filtros_polarizados_cilindro_od: e,
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={70}
                        id="cell"
                        type="type-only-positive-numbers"
                        maxLength={3}
                        size={0}
                        input={currentRefractiveEdit.filtros_polarizados_eje_od}
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            filtros_polarizados_eje_od: e,
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell"></div>
                    <div className="cell cell-label cell-start-content">
                      Ojo D
                    </div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={74}
                        id="cell"
                        type="type-optofloat"
                        size={0}
                        input={currentRefractiveEdit.retrasado_esfera_od}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={props.createPopupQuestion}
                        removePopupQuestion={props.removePopupQuestion}
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            retrasado_esfera_od: e,
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={75}
                        id="cell"
                        type="type-optofloat"
                        size={0}
                        input={currentRefractiveEdit.retrasado_cilindro_od}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={props.createPopupQuestion}
                        removePopupQuestion={props.removePopupQuestion}
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            retrasado_cilindro_od: e,
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={76}
                        id="cell"
                        type="type-only-positive-numbers"
                        maxLength={3}
                        size={0}
                        input={currentRefractiveEdit.retrasado_eje_od}
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            retrasado_eje_od: e,
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell cell-label cell-start-content">
                      Ojo I
                    </div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={71}
                        id="cell"
                        type="type-optofloat"
                        size={0}
                        input={
                          currentRefractiveEdit.filtros_polarizados_esfera_oi
                        }
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={props.createPopupQuestion}
                        removePopupQuestion={props.removePopupQuestion}
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            filtros_polarizados_esfera_oi: e,
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={72}
                        id="cell"
                        type="type-optofloat"
                        size={0}
                        input={
                          currentRefractiveEdit.filtros_polarizados_cilindro_oi
                        }
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={props.createPopupQuestion}
                        removePopupQuestion={props.removePopupQuestion}
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            filtros_polarizados_cilindro_oi: e,
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={73}
                        id="cell"
                        type="type-only-positive-numbers"
                        maxLength={3}
                        size={0}
                        input={currentRefractiveEdit.filtros_polarizados_eje_oi}
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            filtros_polarizados_eje_oi: e,
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell"></div>
                    <div className="cell cell-label cell-start-content">
                      Ojo I
                    </div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={77}
                        id="cell"
                        type="type-optofloat"
                        size={0}
                        input={currentRefractiveEdit.retrasado_esfera_oi}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={props.createPopupQuestion}
                        removePopupQuestion={props.removePopupQuestion}
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            retrasado_esfera_oi: e,
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <InputDataControl
                        tabIndex={78}
                        id="cell"
                        type="type-optofloat"
                        size={0}
                        input={currentRefractiveEdit.retrasado_cilindro_oi}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={props.createPopupQuestion}
                        removePopupQuestion={props.removePopupQuestion}
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            retrasado_cilindro_oi: e,
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell">
                      <InputData
                        tabIndex={79}
                        id="cell"
                        type="type-only-positive-numbers"
                        maxLength={3}
                        size={0}
                        input={currentRefractiveEdit.retrasado_eje_oi}
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            retrasado_eje_oi: e,
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell opt-table-subtitle combined-cells-9 cell-start-content">
                      Observaciones
                    </div>
                    <div className="cell combined-cells-9">
                      <InputTextArea
                        tabIndex={80}
                        maxLength={400}
                        id="refraccion-subjetiva-observaciones"
                        type="type-full"
                        size={0}
                        input={
                          currentRefractiveEdit.observaciones_refraccion_subjetiva_binocular
                        }
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            observaciones_refraccion_subjetiva_binocular: e,
                          });
                        }}
                      ></InputTextArea>
                    </div>
                  </div>
                </div>
                <div
                  className="w-50"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <div
                    className="opt-table-content grid-4"
                    id="control-convergencia"
                  >
                    <div className="cell combined-cells-4"></div>
                    <div className="cell"></div>
                    <div className="cell opt-table-subtitle combined-cells-3 cell-start-content">
                      Control de convergencia
                    </div>
                    <div className="cell"></div>
                    <div className="cell cell-label">Esf</div>
                    <div className="cell cell-label">Cil</div>
                    <div className="cell cell-label">Eje</div>
                    <div className="cell cell-label cell-start-content">
                      Ojo D
                    </div>
                    <div className="cell cell-label">
                      <InputDataControl
                        tabIndex={97}
                        id="cell"
                        type="type-optofloat"
                        size={0}
                        input={
                          currentRefractiveEdit.control_convergencias_esfera_od
                        }
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={props.createPopupQuestion}
                        removePopupQuestion={props.removePopupQuestion}
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            control_convergencias_esfera_od: e,
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell cell-label">
                      <InputDataControl
                        tabIndex={98}
                        id="cell"
                        type="type-optofloat"
                        size={0}
                        input={
                          currentRefractiveEdit.control_convergencias_cilindro_od
                        }
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={props.createPopupQuestion}
                        removePopupQuestion={props.removePopupQuestion}
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            control_convergencias_cilindro_od: e,
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell cell-label">
                      <InputData
                        tabIndex={99}
                        id="cell"
                        type="type-only-positive-numbers"
                        maxLength={3}
                        size={0}
                        input={
                          currentRefractiveEdit.control_convergencias_eje_od
                        }
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            control_convergencias_eje_od: e,
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell cell-label cell-start-content">
                      Ojo I
                    </div>
                    <div className="cell cell-label">
                      <InputDataControl
                        tabIndex={100}
                        id="cell"
                        type="type-optofloat"
                        size={0}
                        input={
                          currentRefractiveEdit.control_convergencias_esfera_oi
                        }
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={props.createPopupQuestion}
                        removePopupQuestion={props.removePopupQuestion}
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            control_convergencias_esfera_oi: e,
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell cell-label">
                      <InputDataControl
                        tabIndex={101}
                        id="cell"
                        type="type-optofloat"
                        size={0}
                        input={
                          currentRefractiveEdit.control_convergencias_cilindro_oi
                        }
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={props.createPopupQuestion}
                        removePopupQuestion={props.removePopupQuestion}
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            control_convergencias_cilindro_oi: e,
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell cell-label">
                      <InputData
                        tabIndex={102}
                        id="cell"
                        type="type-only-positive-numbers"
                        maxLength={3}
                        size={0}
                        input={
                          currentRefractiveEdit.control_convergencias_eje_oi
                        }
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            control_convergencias_eje_oi: e,
                          });
                        }}
                      ></InputData>
                    </div>
                  </div>
                  <div className="opt-table-content grid-4" id="humphriss">
                    <div className="cell combined-cells-4"></div>
                    <div className="cell"></div>
                    <div className="cell opt-table-subtitle combined-cells-3 cell-start-content">
                      Humphriss
                    </div>
                    <div className="cell"></div>
                    <div className="cell cell-label">Esf</div>
                    <div className="cell cell-label">Cil</div>
                    <div className="cell cell-label">Eje</div>
                    <div className="cell cell-label cell-start-content">
                      Ojo D
                    </div>
                    <div className="cell cell-label">
                      <InputDataControl
                        tabIndex={103}
                        id="cell"
                        type="type-optofloat"
                        size={0}
                        input={currentRefractiveEdit.humphriss_esfera_od}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={props.createPopupQuestion}
                        removePopupQuestion={props.removePopupQuestion}
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            humphriss_esfera_od: e,
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell cell-label">
                      <InputDataControl
                        tabIndex={104}
                        id="cell"
                        type="type-optofloat"
                        size={0}
                        input={currentRefractiveEdit.humphriss_cilindro_od}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={props.createPopupQuestion}
                        removePopupQuestion={props.removePopupQuestion}
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            humphriss_cilindro_od: e,
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell cell-label">
                      <InputData
                        tabIndex={105}
                        id="cell"
                        type="type-only-positive-numbers"
                        maxLength={3}
                        size={0}
                        input={currentRefractiveEdit.humphriss_eje_od}
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            humphriss_eje_od: e,
                          });
                        }}
                      ></InputData>
                    </div>
                    <div className="cell cell-label cell-start-content">
                      Ojo I
                    </div>
                    <div className="cell cell-label">
                      <InputDataControl
                        tabIndex={106}
                        id="cell"
                        type="type-optofloat"
                        size={0}
                        input={currentRefractiveEdit.humphriss_esfera_oi}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={props.createPopupQuestion}
                        removePopupQuestion={props.removePopupQuestion}
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            humphriss_esfera_oi: e,
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell cell-label">
                      <InputDataControl
                        tabIndex={107}
                        id="cell"
                        type="type-optofloat"
                        size={0}
                        input={currentRefractiveEdit.humphriss_cilindro_oi}
                        minValue=""
                        maxValue=""
                        step={0.25}
                        numDecimales=""
                        createPopupQuestion={props.createPopupQuestion}
                        removePopupQuestion={props.removePopupQuestion}
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            humphriss_cilindro_oi: e,
                          });
                        }}
                      ></InputDataControl>
                    </div>
                    <div className="cell cell-label">
                      <InputData
                        tabIndex={108}
                        id="cell"
                        type="type-only-positive-numbers"
                        maxLength={3}
                        size={0}
                        input={currentRefractiveEdit.humphriss_eje_oi}
                        onChange={(e) => {
                          setCurrentRefractiveEdit({
                            ...currentRefractiveEdit,
                            humphriss_eje_oi: e,
                          });
                        }}
                      ></InputData>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="popup-screen-box external-container"
              style={{ gridColumn: "4/span 3", gridRow: "2/span 1" }}
            >
              <div className="opt-table-container">
                <div className="opt-table-title">Refracción subjetiva</div>
                <div className="opt-table-content grid-5" id="rx-definitiva">
                  <div className="cell"></div>
                  <div className="cell opt-table-subtitle combined-cells-4 cell-start-content">
                    Rx definitiva
                  </div>
                  <div className="cell"></div>
                  <div className="cell cell-label">Esf</div>
                  <div className="cell cell-label">Cil</div>
                  <div className="cell cell-label">Eje</div>
                  <div className="cell cell-label">Add</div>
                  <div className="cell cell-label cell-start-content">
                    Ojo D
                  </div>
                  <div className="cell">
                    <InputDataControl
                      tabIndex={81}
                      id="cell"
                      type="type-optofloat"
                      size={0}
                      input={currentRefractiveEdit.rx_nueva_esfera_od}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={props.createPopupQuestion}
                      removePopupQuestion={props.removePopupQuestion}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          rx_nueva_esfera_od: e,
                        });
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell">
                    <InputDataControl
                      tabIndex={82}
                      id="cell"
                      type="type-optofloat"
                      size={0}
                      input={currentRefractiveEdit.rx_nueva_cilindro_od}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={props.createPopupQuestion}
                      removePopupQuestion={props.removePopupQuestion}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          rx_nueva_cilindro_od: e,
                        });
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell">
                    <InputData
                      tabIndex={83}
                      id="cell"
                      type="type-only-positive-numbers"
                      maxLength={3}
                      size={0}
                      input={currentRefractiveEdit.rx_nueva_eje_od}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          rx_nueva_eje_od: e,
                        });
                      }}
                    ></InputData>
                  </div>
                  <div className="cell">
                    <InputDataControl
                      tabIndex={84}
                      id="cell"
                      type="type-optofloat"
                      size={0}
                      input={currentRefractiveEdit.rx_nueva_add_od}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={props.createPopupQuestion}
                      removePopupQuestion={props.removePopupQuestion}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          rx_nueva_add_od: e,
                        });
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell cell-label cell-start-content">
                    Ojo I
                  </div>
                  <div className="cell">
                    <InputDataControl
                      tabIndex={85}
                      id="cell"
                      type="type-optofloat"
                      size={0}
                      input={currentRefractiveEdit.rx_nueva_esfera_oi}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={props.createPopupQuestion}
                      removePopupQuestion={props.removePopupQuestion}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          rx_nueva_esfera_oi: e,
                        });
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell">
                    <InputDataControl
                      tabIndex={86}
                      id="cell"
                      type="type-optofloat"
                      size={0}
                      input={currentRefractiveEdit.rx_nueva_cilindro_oi}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={props.createPopupQuestion}
                      removePopupQuestion={props.removePopupQuestion}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          rx_nueva_cilindro_oi: e,
                        });
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell">
                    <InputData
                      tabIndex={87}
                      id="cell"
                      type="type-only-positive-numbers"
                      maxLength={3}
                      size={0}
                      input={currentRefractiveEdit.rx_nueva_eje_oi}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          rx_nueva_eje_oi: e,
                        });
                      }}
                    ></InputData>
                  </div>
                  <div className="cell">
                    <InputDataControl
                      tabIndex={88}
                      id="cell"
                      type="type-optofloat"
                      size={0}
                      input={currentRefractiveEdit.rx_nueva_add_oi}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={props.createPopupQuestion}
                      removePopupQuestion={props.removePopupQuestion}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          rx_nueva_add_oi: e,
                        });
                      }}
                    ></InputDataControl>
                  </div>
                </div>
                <div className="opt-table-content grid-6" id="avsc-avcc-ae">
                  <div className="cell"></div>
                  <div className="cell opt-table-subtitle">AVsc</div>
                  <div className="cell combined-cells-2"></div>
                  <div className="cell opt-table-subtitle">AVcc</div>
                  <div className="cell opt-table-subtitle">
                    <span data-tooltip-id="tt1">AE</span>
                    <TooltipBox
                      id="tt1"
                      content={<div className="cell">Agujero estenopeico</div>}
                    ></TooltipBox>
                  </div>
                  <div className="cell cell-label cell-start-content">
                    Ojo D
                  </div>
                  <div className="cell cell-label">
                    <InputData
                      tabIndex={89}
                      maxLength={9}
                      id="cell"
                      type="type-alphanum-ext"
                      size={0}
                      input={currentRefractiveEdit.av_sc_od}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          av_sc_od: e,
                        });
                      }}
                    ></InputData>
                  </div>
                  <div className="cell"></div>
                  <div className="cell cell-label cell-start-content">
                    Ojo D
                  </div>
                  <div className="cell cell-label">
                    <InputData
                      tabIndex={92}
                      maxLength={9}
                      id="cell"
                      type="type-alphanum-ext"
                      size={0}
                      input={currentRefractiveEdit.av_cc_od}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          av_cc_od: e,
                        });
                      }}
                    ></InputData>
                  </div>
                  <div className="cell cell-label cell-end-content">
                    <InputData
                      tabIndex={95}
                      maxLength={9}
                      id="cell"
                      type="type-alphanum-ext"
                      size={0}
                      input={currentRefractiveEdit.ae_od}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          ae_od: e,
                        });
                      }}
                    ></InputData>
                  </div>
                  <div className="cell cell-label cell-start-content">
                    Ojo I
                  </div>
                  <div className="cell cell-label">
                    <InputData
                      tabIndex={90}
                      maxLength={9}
                      id="cell"
                      type="type-alphanum-ext"
                      size={0}
                      input={currentRefractiveEdit.av_sc_oi}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          av_sc_oi: e,
                        });
                      }}
                    ></InputData>
                  </div>
                  <div className="cell"></div>
                  <div className="cell cell-label cell-start-content">
                    Ojo I
                  </div>
                  <div className="cell cell-label">
                    <InputData
                      tabIndex={93}
                      maxLength={9}
                      id="cell"
                      type="type-alphanum-ext"
                      size={0}
                      input={currentRefractiveEdit.av_cc_oi}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          av_cc_oi: e,
                        });
                      }}
                    ></InputData>
                  </div>
                  <div className="cell cell-label  cell-end-content">
                    <InputData
                      tabIndex={96}
                      maxLength={9}
                      id="cell"
                      type="type-alphanum-ext"
                      size={0}
                      input={currentRefractiveEdit.ae_oi}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          ae_oi: e,
                        });
                      }}
                    ></InputData>
                  </div>
                  <div className="cell cell-label cell-start-content">AO</div>
                  <div className="cell cell-label">
                    <InputData
                      tabIndex={91}
                      maxLength={9}
                      id="cell"
                      type="type-alphanum-ext"
                      size={0}
                      input={currentRefractiveEdit.av_sc_ao}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          av_sc_ao: e,
                        });
                      }}
                    ></InputData>
                  </div>
                  <div className="cell"></div>
                  <div className="cell cell-label cell-start-content">AO</div>
                  <div className="cell cell-label">
                    <InputData
                      tabIndex={94}
                      maxLength={9}
                      id="cell"
                      type="type-alphanum-ext"
                      size={0}
                      input={currentRefractiveEdit.av_cc_ao}
                      onChange={(e) => {
                        setCurrentRefractiveEdit({
                          ...currentRefractiveEdit,
                          av_cc_ao: e,
                        });
                      }}
                    ></InputData>
                  </div>
                  <div className="cell"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
